import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useFormGroups, useSaveContext } from 'react-admin';
import { Box } from '@mui/material';
import { ShsButton } from 'src/components/common';
import useTabbedFormNavigation from 'src/components/common/ShsTabbedFormToolbar/useTabIndex';
import { scrollToPageTop } from 'src/utils';

type TabbedFormToolbarProps = {
    tabGroups: string[];
    saveTitle: string;
};

export const ShsTabbedFormToolbar: FC<TabbedFormToolbarProps> = ({ tabGroups, saveTitle = 'Зберегти' }) => {
    const { tabIndex, navigateToTab } = useTabbedFormNavigation();
    const {
        trigger,
        setValue,
        getValues,
        handleSubmit,
        formState: { isValidating, isSubmitting }
    } = useFormContext();
    const { save } = useSaveContext();
    const { getGroupFields } = useFormGroups();

    const tabGroupName = tabGroups[tabIndex];
    const formGroupFields = getGroupFields(tabGroupName);
    const [formFields, setFormFields] = useState<string[]>([]);

    useEffect(() => {
        // filter out group fields like array input
        setFormFields(
            formGroupFields.filter(
                (field, fieldIndex) =>
                    !formGroupFields.some(
                        (otherField, otherFieldIndex) =>
                            otherFieldIndex !== fieldIndex && otherField.split('.')[0] === field
                    )
            )
        );
    }, [formGroupFields]);

    const handleBackwardBtnClick = () => {
        navigateToTab(tabIndex - 1);
        scrollToPageTop();
    };

    const handleForwardBtnClick = async () => {
        // If we're on the last tab, mark that a submit was attempted so cross-field validators can run
        const isLastTab = tabIndex === tabGroups.length - 1;
        if (isLastTab) {
            setValue('__submitAttempt', true, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
        }

        let groupValid = true;
        let first_error;
        for (let field of formFields) {
            const fieldValid = await trigger(field);

            if (!fieldValid) {
                first_error = !first_error ? field : first_error;
                groupValid = false;
                setValue(field, getValues(field), { shouldTouch: true }); // validation error might not display if not touched
            }
        }

        if (groupValid) {
            if (tabIndex < tabGroups.length - 1) {
                navigateToTab(tabIndex + 1);
                scrollToPageTop();
            } else {
                await handleSubmit((data) => save?.(data))();
            }
        } else {
            if (first_error) {
                const el = document.getElementById(first_error);
                if (el) {
                    el.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }
        }
    };

    return (
        <Box display="flex" gap="12px" alignItems="center" padding="24px 0">
            {tabIndex > 0 && <ShsButton styleType="outline" label="Назад" onClick={handleBackwardBtnClick} />}
            <ShsButton
                label={tabIndex !== tabGroups.length - 1 ? 'Далі' : saveTitle}
                onClick={handleForwardBtnClick}
                disabled={isValidating || isSubmitting}
            />
        </Box>
    );
};
